const STOP = new Set(['the', 'a', 'an', 'i', 'my', 'me', 'we', 'our', 'you', 'your', 'it', 'is', 'are', 'be', 'do', 'does', 'can', 'could',
  'how', 'what', 'why', 'when', 'who', 'to', 'of', 'in', 'on', 'for', 'and', 'or', 'with', 'if', 'this', 'that', 'will', 'would', 'have', 'has']);

const normalise = (s) => s.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[’'`]/g, '');

export const stem = (w) => {
  if (w.length <= 3) return w;
  if (w.endsWith('ies') && w.length > 4) return `${w.slice(0, -3)}y`;
  if (w.endsWith('sses')) return w.slice(0, -2);
  if (w.endsWith('ing') && w.length > 5) return w.slice(0, -3);
  if (w.endsWith('ed') && w.length > 4) return w.slice(0, -2);
  if (w.endsWith('es') && w.length > 4 && /(s|x|z|ch|sh)es$/.test(w)) return w.slice(0, -2);
  if (w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
  return w;
};

const tokens = (s) => normalise(s).split(/[^a-z0-9]+/).filter(Boolean);

// Damerau-Levenshtein (optimal string alignment) with early exit above `max`.
const editDistance = (a, b, max) => {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j += 1) d[0][j] = j;
  for (let i = 1; i <= a.length; i += 1) {
    let rowMin = Infinity;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      rowMin = Math.min(rowMin, d[i][j]);
    }
    if (rowMin > max) return max + 1;
  }
  return d[a.length][b.length];
};

const tolerance = (len) => (len >= 8 ? 2 : len >= 5 ? 1 : 0);

// Precompute searchable text + stemmed vocabulary per item.
export const buildIndex = (items) => items.map((item) => {
  const text = normalise([item.question, item.answer, ...(item.topics || []), ...(item.audience || [])].join(' '));
  const words = tokens(text);
  return { item, text, questionText: normalise(item.question), stems: new Set(words.map(stem)), words: Array.from(new Set(words)) };
});

// 2 = exact term/stem hit, 1 = prefix or typo-tolerant hit, 0 = miss
const termScore = (q, entry) => {
  const qs = stem(q);
  if (entry.stems.has(qs) || entry.text.includes(q)) return 2;
  const tol = tolerance(q.length);
  for (const w of entry.words) {
    if (q.length >= 4 && w.startsWith(q)) return 1;
    if (tol && editDistance(qs, stem(w), tol) <= tol) return 1;
  }
  return 0;
};

/** Returns { matches: items[] ranked by relevance, fuzzy: true when nothing matched literally }. */
export const searchFaqs = (index, rawQuery) => {
  const query = normalise(rawQuery.trim());
  if (!query) return { matches: index.map((e) => e.item), fuzzy: false };
  let terms = tokens(query).filter((t) => !STOP.has(t));
  if (!terms.length) terms = tokens(query);
  if (!terms.length) return { matches: [], fuzzy: false };

  const scored = [];
  index.forEach((entry, order) => {
    const scores = terms.map((t) => termScore(t, entry));
    if (scores.some((s) => s === 0)) return;
    const phrase = entry.text.includes(query) ? 4 : 0;
    const inQuestion = terms.every((t) => entry.questionText.includes(stem(t))) ? 1 : 0;
    const total = phrase + inQuestion + scores.reduce((a, b) => a + b, 0);
    scored.push({ item: entry.item, exact: scores.every((s) => s === 2), total, order });
  });
  scored.sort((a, b) => b.total - a.total || Number(b.item.featured) - Number(a.item.featured) || a.order - b.order);
  return { matches: scored.map((s) => s.item), fuzzy: scored.length > 0 && !scored.some((s) => s.exact) };
};
